import apiClient from './apiClient';

const API_URL = '/analytics';

export interface AnalyticsEvent {
  eventType:
    | 'page_view'
    | 'deal_view'
    | 'deal_click'
    | 'user_signup'
    | 'deal_submit'
    | 'comment_post'
    | 'vote_cast'
    | 'search_performed'
    | 'profile_view'
    | 'badge_earned'
    | 'user_action';
  dealId?: string;
  metadata?: {
    searchQuery?: string;
    pageUrl?: string;
    clickPosition?: number;
    voteType?: 'up' | 'down';
    badgeType?: string;
    action?: string;
    source?: string;
    [key: string]: any;
  };
}

export interface UserEngagementMetrics {
  dailyActiveUsers: Array<{
    _id: string;
    activeUsers: number;
  }>;
  sessionMetrics: Array<{
    _id: string;
    avgDuration: number;
    totalSessions: number;
    avgPageViews: number;
    avgDealViews: number;
  }>;
  pageViewTrends: Array<{
    _id: string;
    pageViews: number;
    uniqueUsers: number;
  }>;
}

export interface DealEngagementMetrics {
  dealViewTrends: Array<{
    _id: string;
    dealViews: number;
    uniqueDeals: number;
  }>;
  dealClickRates: Array<{
    _id: string;
    views: number;
    clicks: number;
    clickThroughRate: number;
  }>;
  mostViewedDeals: Array<{
    _id: string;
    viewCount: number;
    deal: {
      title: string;
      niche: string;
      clickCount: number;
    };
  }>;
}

export interface SearchAnalytics {
  topSearchQueries: Array<{
    _id: string;
    searchCount: number;
    uniqueUsers: number;
  }>;
  searchTrends: Array<{
    _id: string;
    searchCount: number;
    uniqueSearchers: number;
  }>;
}

export interface UserGrowthMetrics {
  registrationTrends: Array<{
    _id: string;
    newUsers: number;
  }>;
  userRetention: {
    totalUsers: number;
    retainedUsers: number;
    retentionRate: number;
    avgDaysSinceSignup: number;
  };
}

class AnalyticsService {
  private static internalSessionId: string | null = null;
  private static internalCurrentUserId: string | null = null;
  private static internalIsServiceAvailable: boolean = true;
  private static internalRetryCount: number = 0;
  private static internalMaxRetries: number = 3;

  /**
   * Check if analytics service is available
   */
  private static async checkServiceHealth(): Promise<boolean> {
    // In test environment, assume service is available to avoid complex mocking
    if (process.env.NODE_ENV === 'test') {
      this.internalIsServiceAvailable = true;
      this.internalRetryCount = 0;
      return true;
    }

    try {
      const response = await apiClient.get(`${API_URL}/health`, {
        timeout: 3000,
      });
      this.internalIsServiceAvailable = response?.status === 200;
      this.internalRetryCount = 0; // Reset retry count on success
      return this.internalIsServiceAvailable;
    } catch (error: any) {
      this.internalIsServiceAvailable = false;

      // Provide more specific error information
      if (error.code === 'ECONNREFUSED') {
        console.warn('Analytics service connection refused - service may not be running');
      } else if (error.code === 'ENOTFOUND') {
        console.warn('Analytics service not found - check URL configuration');
      } else if (error.name === 'TimeoutError') {
        console.warn('Analytics service timeout - service may be slow or unresponsive');
      } else {
        console.warn('Analytics service not available:', error.message || error);
      }

      return false;
    }
  }

  /**
   * Wrapper for analytics requests with error handling
   */
  private static async safeRequest<T>(
    requestFn: () => Promise<T>,
    fallbackValue?: T
  ): Promise<T | undefined> {
    // Check if we should skip due to previous failures
    if (!this.internalIsServiceAvailable && this.internalRetryCount >= this.internalMaxRetries) {
      return fallbackValue;
    }

    try {
      const result = await requestFn();
      this.internalIsServiceAvailable = true;
      this.internalRetryCount = 0;
      return result;
    } catch (error) {
      this.internalRetryCount++;

      if (this.internalRetryCount >= this.internalMaxRetries) {
        this.internalIsServiceAvailable = false;
        console.warn(
          `Analytics service unavailable after ${this.internalMaxRetries} attempts. Disabling analytics tracking.`
        );
      } else {
        console.warn(
          `Analytics request failed (attempt ${this.internalRetryCount}/${this.internalMaxRetries}):`,
          error
        );
      }

      return fallbackValue;
    }
  }

  /**
   * Initialize session tracking with optional user ID
   */
  static async initializeSession(userId?: string): Promise<void> {
    if (!this.internalSessionId) {
      // Try to get existing session from localStorage first
      const storedSessionId = localStorage.getItem('analytics_session_id');
      if (storedSessionId) {
        this.internalSessionId = storedSessionId;
      } else {
        this.internalSessionId = this.generateSessionId();
        localStorage.setItem('analytics_session_id', this.internalSessionId);
      }
    }

    // Update user ID if provided
    if (userId) {
      this.internalCurrentUserId = userId;
    }

    // Wait for analytics service to be ready before starting session
    await this.waitForServiceReady();
    this.startSession();
  }

  /**
   * Wait for analytics service to be ready with exponential backoff
   */
  private static async waitForServiceReady(maxWaitTime: number = 5000): Promise<void> {
    const startTime = Date.now();
    let attempt = 0;
    const maxAttempts = 3; // Reduced from 5

    while (Date.now() - startTime < maxWaitTime && attempt < maxAttempts) {
      attempt++;

      if (process.env.NODE_ENV === 'development') {
        console.info(`⏳ Waiting for analytics service (attempt ${attempt}/${maxAttempts})...`);
      }

      const isReady = await this.checkServiceHealth();

      if (isReady) {
        return; // Service is ready
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 4000);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    // Service not ready after waiting
    this.internalIsServiceAvailable = false;
    console.warn('⚠️ Analytics service not ready after waiting, proceeding with initialization');
  }

  /**
   * Update user ID for current session
   */
  static updateUserId(userId: string | null): void {
    this.internalCurrentUserId = userId;

    // If we have a session and user just logged in, update the session
    if (this.internalSessionId && userId) {
      this.startSession();
    }
  }

  /**
   * Generate a unique session ID
   */
  private static generateSessionId(): string {
    return `session_${  Date.now()  }_${  Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Start a new analytics session with better error handling
   */
  static async startSession(): Promise<void> {
    if (!this.internalSessionId) {
      this.internalSessionId = this.generateSessionId();
      localStorage.setItem('analytics_session_id', this.internalSessionId);
    }

    await this.safeRequest(async () => {
      await apiClient.post(`${API_URL}/session/start`, {
        sessionId: this.internalSessionId,
        userId: this.internalCurrentUserId,
      });
    });
  }

  /**
   * End the current session
   */
  static async endSession(exitPage?: string): Promise<void> {
    if (!this.internalSessionId) return;

    await this.safeRequest(async () => {
      await apiClient.post(`${API_URL}/session/end`, {
        sessionId: this.internalSessionId,
        exitPage,
      });
    });

    // Clear session data regardless of request success
    localStorage.removeItem('analytics_session_id');
    this.internalSessionId = null;
    this.internalCurrentUserId = null;
  }

  /**
   * Track an analytics event with fallback handling
   */
  static async trackEvent(event: AnalyticsEvent): Promise<void> {
    await this.safeRequest(async () => {
      await apiClient.post(`${API_URL}/track`, {
        ...event,
        sessionId: this.internalSessionId,
        userId: this.internalCurrentUserId,
        timestamp: new Date().toISOString(),
      });
    });
  }

  /**
   * Track page view
   */
  static async trackPageView(pageUrl: string): Promise<void> {
    await this.trackEvent({
      eventType: 'page_view',
      metadata: { pageUrl },
    });
  }

  /**
   * Track deal view
   */
  static async trackDealView(dealId: string): Promise<void> {
    await this.trackEvent({
      eventType: 'deal_view',
      dealId,
    });
  }

  /**
   * Track deal click
   */
  static async trackDealClick(dealId: string, clickPosition?: number): Promise<void> {
    await this.trackEvent({
      eventType: 'deal_click',
      dealId,
      metadata: { clickPosition },
    });
  }

  /**
   * Track search
   */
  static async trackSearch(searchQuery: string): Promise<void> {
    await this.trackEvent({
      eventType: 'search_performed',
      metadata: { searchQuery },
    });
  }

  /**
   * Track vote
   */
  static async trackVote(dealId: string, voteType: 'up' | 'down'): Promise<void> {
    await this.trackEvent({
      eventType: 'vote_cast',
      dealId,
      metadata: { voteType },
    });
  }

  /**
   * Track comment
   */
  static async trackComment(dealId: string): Promise<void> {
    await this.trackEvent({
      eventType: 'comment_post',
      dealId,
    });
  }

  /**
   * Track badge earned
   */
  static async trackBadgeEarned(badgeType: string): Promise<void> {
    await this.trackEvent({
      eventType: 'badge_earned',
      metadata: { badgeType },
    });
  }

  /**
   * Get user engagement metrics (admin only)
   */
  static async getUserEngagementMetrics(timeRange: string = '30d'): Promise<UserEngagementMetrics> {
    try {
      const response = await apiClient.get(`${API_URL}/engagement?range=${timeRange}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching user engagement metrics:', error);
      throw error;
    }
  }

  /**
   * Get deal engagement metrics (admin only)
   */
  static async getDealEngagementMetrics(timeRange: string = '30d'): Promise<DealEngagementMetrics> {
    try {
      const response = await apiClient.get(`${API_URL}/deals?range=${timeRange}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching deal engagement metrics:', error);
      throw error;
    }
  }

  /**
   * Get search analytics (admin only)
   */
  static async getSearchAnalytics(timeRange: string = '30d'): Promise<SearchAnalytics> {
    try {
      const response = await apiClient.get(`${API_URL}/search?range=${timeRange}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching search analytics:', error);
      throw error;
    }
  }

  /**
   * Get user growth metrics
   */
  static async getUserGrowthMetrics(timeRange: string = '30d'): Promise<UserGrowthMetrics> {
    try {
      const response = await apiClient.get(`${API_URL}/growth`, {
        params: { timeRange },
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching user growth metrics:', error);
      throw error;
    }
  }

  /**
   * Get performance analytics
   */
  static async getPerformanceMetrics(timeRange: string = '7d'): Promise<any> {
    try {
      const response = await apiClient.get(`${API_URL}/performance`, {
        params: { timeRange },
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
      throw error;
    }
  }

  /**
   * Get real-time analytics metrics
   */
  static async getRealTimeMetrics(): Promise<any> {
    try {
      const response = await apiClient.get(`${API_URL}/realtime`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching real-time metrics:', error);
      throw error;
    }
  }

  /**
   * Export analytics data as CSV
   */
  static async exportAnalyticsCSV(
    dataType: string = 'all',
    timeRange: string = '30d'
  ): Promise<void> {
    try {
      const response = await apiClient.get(`${API_URL}/export/csv`, {
        params: { dataType, timeRange },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics_${dataType}_${timeRange}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      throw error;
    }
  }

  /**
   * Export analytics data as JSON
   */
  static async exportAnalyticsJSON(
    dataType: string = 'all',
    timeRange: string = '30d'
  ): Promise<void> {
    try {
      const response = await apiClient.get(`${API_URL}/export/json`, {
        params: { dataType, timeRange },
      });

      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics_${dataType}_${timeRange}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting JSON:', error);
      throw error;
    }
  }

  /**
   * Export user's personal data (GDPR compliance)
   */
  static async exportUserData(): Promise<void> {
    try {
      const response = await apiClient.get(`${API_URL}/export/user-data`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `user_data_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting user data:', error);
      throw error;
    }
  }

  /**
   * Track page performance manually
   */
  static async trackPagePerformanceManual(performanceData: {
    pageUrl: string;
    loadTime: number;
    domContentLoaded: number;
    firstContentfulPaint: number;
    largestContentfulPaint: number;
    sessionId?: string;
  }): Promise<void> {
    try {
      await apiClient.post(`${API_URL}/performance/page`, {
        ...performanceData,
        sessionId: performanceData.sessionId || this.internalSessionId,
      });
    } catch (error) {
      console.error('Error tracking page performance:', error);
      throw error;
    }
  }

  /**
   * Track API performance with error handling
   */
  static async trackAPIPerformanceManual(performanceData: {
    endpoint: string;
    method: string;
    responseTime: number;
    statusCode: number;
  }): Promise<void> {
    await this.safeRequest(async () => {
      await apiClient.post(`${API_URL}/performance/api`, performanceData);
    });
  }
}

export default AnalyticsService;

// Utility functions for local analytics tracking
const isTrackingEnabled = (): boolean => {
  // Could be disabled for privacy or performance reasons
  return true;
};

const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${  Date.now()  }_${  Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

const getStoredData = (key: string): any[] => {
  try {
    const data = localStorage.getItem(`analytics_${key}`);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.warn(`Failed to get stored data for ${key}:`, error);
    return [];
  }
};

const storeData = (key: string, data: any[]): void => {
  try {
    localStorage.setItem(`analytics_${key}`, JSON.stringify(data));
  } catch (error) {
    console.warn(`Failed to store data for ${key}:`, error);
  }
};

// Search Analytics Functions
export const trackSearchQuery = (query: string, resultsCount: number, filters?: any) => {
  if (!isTrackingEnabled()) return;

  try {
    const searchData = {
      query: query.toLowerCase().trim(),
      resultsCount,
      filters: filters || {},
      timestamp: Date.now(),
      sessionId: getSessionId(),
      url: window.location.pathname + window.location.search,
    };

    // Store locally for now (can be sent to analytics service later)
    const searches = getStoredData('user_searches') || [];
    searches.push(searchData);

    // Keep only last 100 searches to prevent storage bloat
    const recentSearches = searches.slice(-100);
    storeData('user_searches', recentSearches);

    // Search event tracked
  } catch (error) {
    console.warn('Failed to track search:', error);
  }
};

export const trackSearchResult = (query: string, dealId: string, position: number) => {
  if (!isTrackingEnabled()) return;

  try {
    const resultData = {
      query: query.toLowerCase().trim(),
      dealId,
      position,
      timestamp: Date.now(),
      sessionId: getSessionId(),
    };

    const interactions = getStoredData('search_interactions') || [];
    interactions.push(resultData);

    // Keep only last 200 interactions
    const recentInteractions = interactions.slice(-200);
    storeData('search_interactions', recentInteractions);

    // Search result interaction tracked
  } catch (error) {
    console.warn('Failed to track search result:', error);
  }
};

export const trackSearchFilterUsage = (filterType: string, filterValue: any) => {
  if (!isTrackingEnabled()) return;

  try {
    const filterData = {
      filterType,
      filterValue,
      timestamp: Date.now(),
      sessionId: getSessionId(),
    };

    const filterUsage = getStoredData('filter_usage') || [];
    filterUsage.push(filterData);

    // Keep only last 100 filter interactions
    const recentFilterUsage = filterUsage.slice(-100);
    storeData('filter_usage', recentFilterUsage);

    // Filter usage tracked
  } catch (error) {
    console.warn('Failed to track filter usage:', error);
  }
};

export const getSearchAnalytics = () => {
  try {
    return {
      searches: getStoredData('user_searches') || [],
      interactions: getStoredData('search_interactions') || [],
      filterUsage: getStoredData('filter_usage') || [],
    };
  } catch (error) {
    console.warn('Failed to get search analytics:', error);
    return { searches: [], interactions: [], filterUsage: [] };
  }
};

/**
 * Analytics Export Service (Frontend)
 */
export class AnalyticsExportService {
  /**
   * Export analytics data as CSV
   */
  static async exportCSV(dataType: string = 'all', timeRange: string = '30d'): Promise<void> {
    return AnalyticsService.exportAnalyticsCSV(dataType, timeRange);
  }

  /**
   * Export analytics data as JSON
   */
  static async exportJSON(dataType: string = 'all', timeRange: string = '30d'): Promise<void> {
    return AnalyticsService.exportAnalyticsJSON(dataType, timeRange);
  }

  /**
   * Export user's personal data (GDPR compliance)
   */
  static async exportUserData(): Promise<void> {
    return AnalyticsService.exportUserData();
  }
}

/**
 * Real-time Analytics Service (Frontend)
 */
export class RealTimeAnalyticsService {
  private static internalEventSource: EventSource | null = null;
  private static internalListeners: Set<(data: any) => void> = new Set();
  private static internalPollingInterval: NodeJS.Timeout | null = null;
  private static internalLastData: any = null;

  /**
   * Get current real-time metrics
   */
  static async getCurrentMetrics(): Promise<any> {
    try {
      const data = await AnalyticsService.getRealTimeMetrics();
      this.internalLastData = data;
      this.notifyListeners(data);
      return data;
    } catch (error) {
      console.error('Error fetching real-time metrics:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time analytics updates
   */
  static subscribe(callback: (data: any) => void): () => void {
    this.internalListeners.add(callback);

    // Start polling if this is the first listener
    if (this.internalListeners.size === 1) {
      this.startPolling();
    }

    // Return unsubscribe function
    return () => {
      this.internalListeners.delete(callback);

      // Stop polling if no more listeners
      if (this.internalListeners.size === 0) {
        this.stopPolling();
      }
    };
  }

  /**
   * Start polling for real-time data
   */
  private static startPolling(): void {
    // Check if polling is enabled via environment variable
    const pollingEnabled = process.env.REACT_APP_ANALYTICS_POLLING_ENABLED === 'true';
    const pollingInterval = parseInt(
      process.env.REACT_APP_ANALYTICS_POLLING_INTERVAL || '5000',
      10
    );

    if (!pollingEnabled) {
      console.log('[Analytics] Real-time polling is disabled');
      return;
    }

    if (this.internalPollingInterval) {
      return; // Already polling
    }

    console.log(`[Analytics] Starting real-time polling with ${pollingInterval}ms interval`);
    this.internalPollingInterval = setInterval(async () => {
      try {
        const data = await this.getCurrentMetrics();
        if (JSON.stringify(data) !== JSON.stringify(this.internalLastData)) {
          this.internalLastData = data;
          this.notifyListeners(data);
        }
      } catch (error) {
        console.warn('Failed to fetch real-time metrics:', error);
      }
    }, pollingInterval);
  }

  /**
   * Stop polling for real-time data
   */
  private static stopPolling(): void {
    if (this.internalPollingInterval) {
      clearInterval(this.internalPollingInterval);
      this.internalPollingInterval = null;
    }
  }

  /**
   * Notify all listeners with new data
   */
  private static notifyListeners(data: any): void {
    this.internalListeners.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.warn('Error in real-time analytics callback:', error);
      }
    });
  }
}

/**
 * Performance Analytics Service (Frontend)
 */
export class PerformanceAnalyticsService {
  /**
   * Track page performance automatically
   */
  static trackPagePerformance(pageUrl: string): void {
    if (typeof window === 'undefined' || !window.performance) return;

    // Wait for page to fully load
    window.addEventListener('load', () => {
      setTimeout(async () => {
        const perfData = window.performance.timing;
        // const navigationEntry = window.performance.getEntriesByType(
        //   'navigation'
        // )[0] as PerformanceNavigationTiming;

        const data = {
          pageUrl,
          loadTime: perfData.loadEventEnd - perfData.navigationStart,
          domContentLoaded: perfData.domContentLoadedEventEnd - perfData.navigationStart,
          firstContentfulPaint: this.getFirstContentfulPaint(),
          largestContentfulPaint: await this.getLargestContentfulPaint(),
        };

        AnalyticsService.trackPagePerformanceManual(data).catch((error) => {
          console.error('Error tracking page performance:', error);
        });
      }, 100);
    });
  }

  /**
   * Track API performance
   */
  static trackAPIPerformance(
    endpoint: string,
    method: string,
    responseTime: number,
    statusCode: number
  ): void {
    AnalyticsService.trackAPIPerformanceManual({
      endpoint,
      method,
      responseTime,
      statusCode,
    }).catch((error) => {
      console.error('Error tracking API performance:', error);
    });
  }

  /**
   * Get performance metrics from server
   */
  static async getPerformanceMetrics(timeRange: string = '7d'): Promise<any> {
    return AnalyticsService.getPerformanceMetrics(timeRange);
  }

  /**
   * Get First Contentful Paint metric
   */
  private static getFirstContentfulPaint(): number {
    try {
      const entries = window.performance.getEntriesByName('first-contentful-paint');
      return entries.length > 0 ? entries[0].startTime : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Get Largest Contentful Paint metric
   */
  private static async getLargestContentfulPaint(): Promise<number> {
    try {
      return new Promise((resolve) => {
        try {
          const observer = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            if (entries.length > 0) {
              const lastEntry = entries[entries.length - 1];
              resolve(lastEntry.startTime);
              observer.disconnect();
            }
          });

          observer.observe({ entryTypes: ['largest-contentful-paint'] });

          // Fallback timeout - resolve with 0 if no LCP is captured within 2 seconds
          setTimeout(() => {
            observer.disconnect();
            resolve(0);
          }, 2000);
        } catch (error) {
          resolve(0);
        }
      });
    } catch {
      return 0;
    }
  }
}

/**
 * Enhanced Analytics Tracking
 */
export const enhancedAnalytics = {
  /**
   * Initialize enhanced analytics
   */
  init(): void {
    // Track page performance for current page
    PerformanceAnalyticsService.trackPagePerformance(window.location.pathname);

    // Set up API performance tracking
    this.setupAPIPerformanceTracking();
  },

  /**
   * Set up automatic API performance tracking
   */
  setupAPIPerformanceTracking(): void {
    if (typeof window === 'undefined') return;

    // Intercept fetch requests
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const startTime = performance.now();
      const url = args[0] as string;
      const options = args[1] || {};
      const method = options.method || 'GET';

      try {
        const response = await originalFetch(...args);
        const endTime = performance.now();
        const responseTime = endTime - startTime;

        // Only track API calls to our backend
        if (url.includes('/api/')) {
          PerformanceAnalyticsService.trackAPIPerformance(
            url,
            method,
            responseTime,
            response.status
          );
        }

        return response;
      } catch (error) {
        const endTime = performance.now();
        const responseTime = endTime - startTime;

        // Track failed requests
        if (url.includes('/api/')) {
          PerformanceAnalyticsService.trackAPIPerformance(
            url,
            method,
            responseTime,
            0 // Unknown status for failed requests
          );
        }

        throw error;
      }
    };
  },
};
